import { hasErrorField } from '@/utils/error';
import { SupportedLanguageType } from '@/utils/intl';
import { useEffect, useRef } from 'react';
import { SetMediaEl } from '../../types';

const styleVideoElement = {
    display: 'none',
};

interface AudioProps {
    sources?: { src: string; type: string }[];
    options?: {
        loop?: boolean;
        autoplay?: boolean;
        controls?: boolean;
    };
    setHasAutoPlayRestriction: (hasAutoPlayRestriction: boolean) => void;
    subtitleSources?: { src: string; type: string }[];
    setMediaEl?: SetMediaEl;
    needsCredentials?: boolean;
}
export function Audio({
    options,
    sources,
    subtitleSources,
    setMediaEl,
    needsCredentials = false,
    setHasAutoPlayRestriction,
}: AudioProps) {
    const audioRef = useRef<HTMLVideoElement>(null);

    useEffect(() => {
        const audioEl = audioRef.current;
        setMediaEl?.(audioEl);

        return () => {
            audioEl?.pause();
            setMediaEl?.(null);
        };
    }, [audioRef, setMediaEl]);

    useEffect(() => {
        if (sources && audioRef.current) {
            // Remove existing sources
            audioRef.current.innerHTML = '';

            // Audio source
            sources.forEach(({ src, type }) => {
                const sourceElement = document.createElement('source');
                sourceElement.setAttribute('src', src);
                sourceElement.setAttribute('type', type);
                audioRef.current?.appendChild(sourceElement);
            });

            // Subtitles https://stackoverflow.com/questions/23475335/subtitles-for-audio-with-track-how-to-display-the-subtitles
            if (subtitleSources) {
                subtitleSources.forEach(({ src }) => {
                    const track = document.createElement('track');
                    track.src = src;
                    track.kind = 'captions';
                    track.srclang = SupportedLanguageType.EN;
                    track.track.mode = 'showing';
                    audioRef.current?.appendChild(track);
                });
            }
            if (options?.autoplay) {
                const autoPlayPromise = audioRef.current?.play();
                if (autoPlayPromise !== undefined) {
                    autoPlayPromise
                        .then(() => {
                            setHasAutoPlayRestriction(false);
                        })
                        .catch((error: unknown) => {
                            // play() rejects with a DOMException, which is not
                            // `instanceof Error` in some engines, so narrow by
                            // shape rather than constructor.
                            if (
                                hasErrorField(error, 'name') &&
                                error.name === 'NotAllowedError'
                            ) {
                                // Handle autoplay restriction
                                setHasAutoPlayRestriction(true);
                            }
                        });
                }
            }
        }
    }, [
        options?.autoplay,
        setHasAutoPlayRestriction,
        sources,
        subtitleSources,
    ]);

    useEffect(() => {
        const audioEl = audioRef.current;
        if (!audioEl || !options?.loop) {
            return;
        }

        const handleEnded = () => {
            audioEl.currentTime = 0;
            audioEl.play().catch(() => {});
        };

        audioEl.addEventListener('ended', handleEnded);
        return () => {
            audioEl.removeEventListener('ended', handleEnded);
        };
    }, [options?.loop]);

    return (
        <video
            style={styleVideoElement}
            ref={audioRef}
            crossOrigin={needsCredentials ? 'use-credentials' : 'anonymous'}
        />
    );
}
